import { Role, SessionActivity } from '@shared/entities';
import { holidayDates, WorkTimeType } from '@shared/types';
import dayjs, { Dayjs } from 'dayjs';
import { getIsExtra } from './session-helpers';

const MS_PER_HOUR = 1000 * 60 * 60;

/**
 * Work Time Types by Hours
 */
export const getIsStraightTime = (hours: number) => hours > 0 && hours <= 8;
export const getIsTimeAndOneHalf = (hours: number) => hours > 8 && hours <= 10;
export const getIsDoubleTime = (hours: number) => hours > 10;
export const getIsOvertime = (hours: number) =>
  getIsTimeAndOneHalf(hours) || getIsDoubleTime(hours);

export const getWorkTimeType = (hours: number, totalMultiplier?: number) => {
  if (getIsTimeAndOneHalf(hours)) return WorkTimeType.TimeAndOneHalf;
  else if (getIsDoubleTime(hours))
    return totalMultiplier && totalMultiplier + 1 > 2
      ? WorkTimeType.TimeAndOneHalf
      : WorkTimeType.DoubleTime;
  else return WorkTimeType.StraightTime;
};

/**
 * Day of Week
 *
 * Note: moment's `.weekday()` is locale-aware; dayjs's `.day()` is not. Since neither
 * moment nor dayjs ever has a non-default locale set anywhere in this codebase, moment's
 * default `'en'` locale (week starts Sunday) makes `.weekday()` numerically identical to
 * `.day()` here (Sunday=0…Saturday=6), so this is a literal substitution, not an
 * approximation.
 */
export const getIsSaturday = (date: Dayjs | Date | string) =>
  (dayjs.isDayjs(date) ? date : dayjs(date)).day() === 6;
export const getIsSunday = (date: Dayjs | Date | string) =>
  (dayjs.isDayjs(date) ? date : dayjs(date)).day() === 0;
export const getIsWeekend = (date: Dayjs | Date | string) =>
  getIsSaturday(date) || getIsSunday(date);

/**
 * Time of Day
 */
export const getIsNight = (role: Role, date: Dayjs | Date | string) => {
  const currentHour = (dayjs.isDayjs(date) ? date : dayjs(date)).hour();
  return (
    (currentHour >= 20 && currentHour < 24) ||
    (currentHour >= 0 &&
      currentHour < (getIsExtra(role.performer_type) ? 1 : 6))
  );
};
export const getIsLateNight = (role: Role, date: Dayjs | Date | string) => {
  const currentHour = (dayjs.isDayjs(date) ? date : dayjs(date)).hour();
  return getIsExtra(role.performer_type)
    ? currentHour >= 1 && currentHour < 6
    : false;
};
export const getNightHours = (activity: SessionActivity) => {
  const nightStart = dayjs(activity.start_time)
    .hour(20)
    .minute(0)
    .second(0)
    .millisecond(0);
  return dayjs(activity.end_time).diff(nightStart) / MS_PER_HOUR;
};

/**
 * Holidays
 */
export const getIsHoliday = (date: Dayjs | Date | string): boolean => {
  date = dayjs.isDayjs(date) ? date : dayjs(date);
  let isHoliday = false;

  const month = date.month();
  const day = date.day();
  // See the Day of Week note above - `.day()` is the correct dayjs equivalent of moment's
  // locale-aware `.weekday()` given this codebase never customizes locale.
  const weekday = date.day();

  if (holidayDates[month]) {
    holidayDates[month].forEach((holiday) => {
      if (holiday.day && holiday.day === day) isHoliday = true;
      else if (holiday.days) {
        if (
          day >= holiday.days[0] &&
          day <= holiday.days[1] &&
          holiday.weekday === weekday
        )
          isHoliday = true;
      }
    });
  }

  return isHoliday;
};
